import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSupabase } from '../contexts/SupabaseContext';

interface FormResponse {
  id: string;
  patient_name: string;
  form_template_id: string;
  form_template_name: string;
  responses: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface Question {
  id: string;
  text: string;
  type: string;
  required: boolean;
  options?: string[];
  translations?: {
    [language: string]: {
      text?: string;
      options?: string[];
    }
  };
}

interface FormTemplate {
  id: string;
  name: string;
  description: string;
  config: {
    questions: Question[];
  };
  is_active: boolean;
}

const FormResponses: React.FC = () => {
  const { supabase } = useSupabase();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedResponse, setSelectedResponse] = useState<FormResponse | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateQuestions, setTemplateQuestions] = useState<Question[]>([]);
  const [exporting, setExporting] = useState(false);
  const rowsPerPage = 10;

  // Fetch form responses
  const { data: responsesData, isLoading, error, refetch } = useQuery(
    ['form-responses', page, searchTerm],
    async (): Promise<{ data: FormResponse[]; count: number }> => {
      let query = supabase
        .from('form_responses')
        .select('*')
        .order('created_at', { ascending: false });

      if (searchTerm) {
        query = query.or(`patient_name.ilike.%${searchTerm}%,form_template_name.ilike.%${searchTerm}%`);
      }

      const { data, error, count } = await query
        .range((page - 1) * rowsPerPage, page * rowsPerPage - 1)
        .limit(rowsPerPage);

      if (error) {
        console.error('Error fetching form responses:', error);
        throw error;
      }

      console.log('Form responses fetched:', { data, count });
      return { data: (data as FormResponse[]) || [], count: count || 0 };
    }
  );

  const handleViewResponse = async (response: FormResponse) => {
    console.log('Selected response:', response);
    console.log('Response data:', response.responses);
    setSelectedResponse(response);
    setViewDialogOpen(true);
    
    // Fetch the template to get question definitions
    setTemplateLoading(true);
    try {
      const { data: template, error } = await supabase
        .from('form_templates')
        .select('config')
        .eq('id', response.form_template_id)
        .single() as { data: { config: { questions: Question[] } } | null; error: any };
      
      if (error) {
        console.error('Error fetching template:', error);
        setTemplateQuestions([]);
      } else {
        const questions = template?.config?.questions || [];
        setTemplateQuestions(questions);
      }
    } catch (err) {
      console.error('Error fetching template:', err);
      setTemplateQuestions([]);
    } finally {
      setTemplateLoading(false);
    }
  };

  const getQuestionText = (questionId: string): string => {
    const question = templateQuestions.find(q => q.id === questionId);
    return question ? question.text : questionId;
  };

  const exportToCSV = async () => {
    setExporting(true);
    try {
      // Fetch all responses (not just current page)
      const { data: allResponses, error } = await supabase
        .from('form_responses')
        .select('*')
        .order('created_at', { ascending: false }) as { data: FormResponse[] | null; error: any };

      if (error) {
        console.error('Error fetching responses for export:', error);
        return;
      }

      if (!allResponses || allResponses.length === 0) {
        alert('No responses to export');
        return;
      }

      // Get all unique question IDs from all responses
      const allQuestionIds = new Set<string>();
      allResponses.forEach(response => {
        try {
          const parsedResponses = typeof response.responses === 'string' 
            ? JSON.parse(response.responses) 
            : response.responses;
          Object.keys(parsedResponses).forEach(key => allQuestionIds.add(key));
        } catch (err) {
          console.error('Error parsing response:', err);
        }
      });

      // Fetch templates for all responses to get question texts
      const templateIds = Array.from(new Set(allResponses.map(r => r.form_template_id)));
      const templatesMap = new Map<string, Question[]>();
      
      for (const templateId of templateIds) {
        try {
          const { data: template } = await supabase
            .from('form_templates')
            .select('config')
            .eq('id', templateId)
            .single() as { data: { config: { questions: Question[] } } | null; error: any };
          
          if (template?.config?.questions) {
            templatesMap.set(templateId, template.config.questions);
          }
        } catch (err) {
          console.error('Error fetching template:', err);
        }
      }

      // Create CSV headers
      const headers = [
        'Response ID',
        'Patient Name',
        'Form Template',
        'Submitted Date',
        'Last Updated',
        ...Array.from(allQuestionIds).map(qId => {
          // Try to find question text from any template
          const allQuestions: Question[] = [];
          templatesMap.forEach(questions => {
            allQuestions.push(...questions);
          });
          const question = allQuestions.find((q: Question) => q.id === qId);
          if (question) {
            return question.text;
          }
          return qId; // Fallback to ID if not found
        })
      ];

      // Create CSV rows
      const rows = allResponses.map(response => {
        try {
          const parsedResponses = typeof response.responses === 'string' 
            ? JSON.parse(response.responses) 
            : response.responses;
          
          const row = [
            response.id,
            response.patient_name,
            response.form_template_name,
            new Date(response.created_at).toLocaleString(),
            new Date(response.updated_at).toLocaleString(),
            ...Array.from(allQuestionIds).map(qId => {
              const value = parsedResponses[qId];
              if (value === null || value === undefined) {
                return '';
              }
              // Handle multiple answers (arrays)
              if (Array.isArray(value)) {
                // Use pipe separator with quotes for Excel compatibility
                return `"${value.join(' | ')}"`;
              }
              return String(value);
            })
          ];
          return row;
        } catch (err) {
          console.error('Error processing response:', err);
          return [
            response.id,
            response.patient_name,
            response.form_template_name,
            new Date(response.created_at).toLocaleString(),
            new Date(response.updated_at).toLocaleString(),
            ...Array.from(allQuestionIds).map(() => 'ERROR')
          ];
        }
      });

      // Convert to CSV format
      const csvContent = [
        headers.map(h => `"${h.replace(/"/g, '""')}"`).join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      // Download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `form_responses_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Error exporting data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const totalPages = Math.ceil((responsesData?.count || 0) / rowsPerPage);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading form responses...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error loading form responses
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {error instanceof Error ? error.message : 'Unknown error occurred'}
          </Typography>
          <Button variant="outlined" onClick={() => refetch()}>
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Form Responses
        </Typography>
        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
          onClick={exportToCSV}
          disabled={exporting || !responsesData?.data?.length}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </Box>

      {/* Summary Card */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Responses
              </Typography>
              <Typography variant="h4">
                {responsesData?.count || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search by patient name or form template..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Responses Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Patient</TableCell>
              <TableCell>Form Template</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {responsesData?.data?.map((response) => (
              <TableRow key={response.id} hover>
                <TableCell>
                  <Typography variant="subtitle2">
                    {response.patient_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {response.form_template_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(response.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => handleViewResponse(response)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* View Response Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Form Response Details
        </DialogTitle>
        <DialogContent>
          {selectedResponse && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Patient Information
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Name:</strong> {selectedResponse.patient_name}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Form Responses
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {templateLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      Loading question details...
                    </Typography>
                  </Box>
                ) : (() => {
                  try {
                    const parsedResponses = typeof selectedResponse.responses === 'string' 
                      ? JSON.parse(selectedResponse.responses) 
                      : selectedResponse.responses;
                    
                    return Object.entries(parsedResponses).map(([key, value]) => {
                      const questionText = getQuestionText(key);
                      return (
                        <Box key={key} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          <Typography variant="subtitle2" color="primary">
                            {questionText}
                          </Typography>
                          <Typography variant="body2">
                            {Array.isArray(value) ? value.join(', ') : String(value)}
                          </Typography>
                        </Box>
                      );
                    });
                  } catch (error) {
                    return (
                      <Typography variant="body2" color="error">
                        Error parsing responses: {String(error)}
                      </Typography>
                    );
                  }
                })()}
              </Box>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Submission Details
              </Typography>
              <Typography variant="body2">
                <strong>Submitted:</strong> {formatDate(selectedResponse.created_at)}<br />
                <strong>Last Updated:</strong> {formatDate(selectedResponse.updated_at)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormResponses;
